import { DsdFile } from "@/types/dsd";
import { SearchIcon } from "@/components/icons/search";
import {
  PeriodChip,
  ReportPeriod,
  ReportType,
  TypeChip,
  YearChip,
} from "@/components/common/report-chip";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { cn } from "@/lib/utils";
import { filterBySearchText } from "@/utils/search-text";
import { Button } from "@/toggle-ui-kit/button";
import { RadioButton } from "@/toggle-ui-kit/control";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  ForwardModalDescription,
  ForwardModalFooter,
  ForwardModalHeader,
} from "./forward-modal-chrome";

const searchHaystack = (report: DsdFile) =>
  [report.label, report.displayName, report.name].filter(Boolean).join(" ");

/** 보고서 한 줄 — Figma 5611:340843(기본) / 5611:340519(선택). */
const ReportRow = ({
  report,
  selected,
  onSelect,
}: {
  report: DsdFile;
  selected: boolean;
  onSelect: () => void;
}) => {
  const year = report.reportYear ?? "-";
  const period = report.reportQuarter as ReportPeriod | null;
  const reportType = report.financialStatementType as ReportType | null;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        // Figma 의 stroke 는 안쪽으로 그려져 크기를 늘리지 않는다 → border 대신 inset ring.
        "flex w-full shrink-0 items-center gap-4 rounded-[8px] py-4 pl-4 pr-3 text-left ring-1 ring-inset transition-colors",
        selected
          ? "bg-blue-50 ring-blue-500"
          : "bg-static-white ring-line-normal hover:bg-background-neutral-normal",
      )}
    >
      <RadioButton
        checked={selected}
        size={20}
        tabIndex={-1}
        className="pointer-events-none"
        aria-hidden
      />
      <span className="flex shrink-0 items-center gap-1">
        {reportType && <TypeChip type={reportType} />}
        <YearChip year={year} />
        {period && <PeriodChip period={period} />}
      </span>
      <span className="label-1-normal-medium min-w-0 flex-1 truncate text-label-normal">
        {report.displayName || report.name}
      </span>
    </button>
  );
};

/** 목록 자리에 그대로 들어가는 안내 문구 — 로딩·에러·빈 결과 공용. */
const ListPlaceholder = ({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "negative";
}) => (
  <div
    className={cn(
      "flex h-full items-center justify-center gap-2 caption-1-regular",
      tone === "negative" ? "text-status-negative" : "text-label-alternative",
    )}
  >
    {children}
  </div>
);

export interface ReportSelectStepProps {
  reports: DsdFile[];
  selectedReport: DsdFile | null;
  onSelectReport: (report: DsdFile | null) => void;
  isLoading?: boolean;
  isError?: boolean;
  isConfirming?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * XBRL 태그 불러오기 1단계 — 이월할 원본 보고서 고르기. Figma 5611:340832 / 5611:340508.
 * 모달 높이(550px)를 고정하고 목록만 스크롤한다.
 */
export const ReportSelectStep = ({
  reports,
  selectedReport,
  onSelectReport,
  isLoading = false,
  isError = false,
  isConfirming = false,
  onCancel,
  onConfirm,
}: ReportSelectStepProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredReports = filterBySearchText(
    reports,
    searchQuery,
    searchHaystack,
  );

  const handleSelect = (report: DsdFile) => {
    // 이미 선택된 항목을 다시 누르면 선택 해제.
    onSelectReport(selectedReport?.id === report.id ? null : report);
  };

  return (
    <ModalWrapper className="flex h-[550px] max-h-[calc(100vh-48px)] w-[600px] flex-col gap-0 overflow-hidden rounded-[8px] border-0 bg-background-base p-0 shadow-xxlarge ring-1 ring-inset ring-line-normal">
      <ForwardModalHeader title="XBRL 태그 불러오기" />
      <ForwardModalDescription>
        이전 보고서의 태깅 정보를 덮어씁니다.
      </ForwardModalDescription>

      <div className="flex min-h-0 flex-1 flex-col gap-2 px-6 pb-4 pt-2">
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex h-[39px] w-full shrink-0 items-center gap-2 overflow-hidden rounded-[8px] bg-background-base px-3 py-2 ring-1 ring-inset ring-line-normal">
            <span className="flex size-3.5 shrink-0 items-center justify-center [&>svg]:size-full">
              <SearchIcon />
            </span>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="보고서 이름으로 검색"
              className="min-w-0 flex-1 bg-transparent text-[1rem] leading-[1.429] tracking-[0.0145em] text-label-normal outline-none placeholder:text-neutral-400"
            />
          </div>

          <ScrollbarArea
            className="min-h-0 w-full flex-1"
            viewportClassName="[&>div]:!block"
          >
            {isLoading ? (
              <ListPlaceholder>
                <Loader2 className="size-4 animate-spin" />
                검색 중입니다...
              </ListPlaceholder>
            ) : isError ? (
              <ListPlaceholder tone="negative">
                * 에러가 발생하였습니다. 다시 검색해주세요.
              </ListPlaceholder>
            ) : filteredReports.length === 0 ? (
              <ListPlaceholder>* 검색 결과가 없습니다.</ListPlaceholder>
            ) : (
              <div className="flex w-full flex-col gap-2">
                {filteredReports.map((report) => (
                  <ReportRow
                    key={report.id}
                    report={report}
                    selected={selectedReport?.id === report.id}
                    onSelect={() => handleSelect(report)}
                  />
                ))}
              </div>
            )}
          </ScrollbarArea>
        </div>

        <div className="flex w-full shrink-0 items-start gap-1 rounded-[6px] bg-background-neutral-alternative p-2">
          <p className="caption-1-regular flex-1 text-label-neutral">
            * 이전에 태깅을 한 보고서가 있을 경우, 해당 보고서의 XBRL 태그를
            동일한 위치에 불러와서 덮어 씌웁니다.
            <br />* 이전에 태깅하지 않은 부분에 대해서만 새롭게 태깅 작업을
            진행하면 됩니다.
            <br />* 기존 데이터를 덮어쓰면 작업한 태그가 사라질 수 있습니다.
          </p>
        </div>
      </div>

      <ForwardModalFooter>
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          onClick={onCancel}
        >
          취소
        </Button>
        <Button
          variant="solid"
          color="primary"
          size="base"
          disabled={!selectedReport || isConfirming}
          leftIcon={isConfirming && <Loader2 className="size-4 animate-spin" />}
          onClick={onConfirm}
        >
          다음
        </Button>
      </ForwardModalFooter>
    </ModalWrapper>
  );
};
